/**
 * Chat scope objects. Scope is the v2 contract between the command bar and
 * the (unchanged) backend: slide-focused chat and deck-wide chat share one
 * SSE driver server-side but keep different endpoints, tool sets, and apply
 * contracts (slide: edit_slide/swap_primitive + ifMatch hash; deck: the
 * structural tools plus edit_slide for any slide, no ifMatch). The UI never asks the user to
 * pick — a focused slide means slide scope (shown as a clearable chip),
 * no focus means deck scope.
 */

import type { SlideEntry } from '../../shared/deck.ts';

export type ChatScopeKind = 'slide' | 'deck' | 'selection';

/**
 * What every scope carries. `key` is the turn store's slice id, and it is NOT
 * one-to-one with `kind`: deck and selection scope deliberately share
 * `<slug>:_deck`, so their cards land in one thread and the one-turn-per-key
 * gate serialises them.
 */
export type ChatScope = {
  kind: ChatScopeKind;
  key: string;
  slug: string;
  /** The focused slide — null for deck and selection scope, which act on many. */
  slide: SlideEntry | null;
  chatPath: string;
  threadPath: string;
  label: string;
  placeholder: string;
  /** Selection scope only: the ids the server restricts the turn to. */
  selection?: string[];
  selectionSlides?: SlideEntry[];
};

export function slideScope(slug: string, slide: SlideEntry): ChatScope {
  const id = encodeURIComponent(slide.id);
  const base = `/api/chat/${slug}/_slide/${id}`;
  return {
    kind: 'slide',
    key: `${slug}:${slide.id}`,
    slug,
    slide,
    chatPath: base,
    threadPath: `${base}/thread`,
    label: slide.title || slide.id,
    placeholder: 'Ask for a tighter title, a punchier body, a different primitive…',
  };
}

export function deckScope(slug: string): ChatScope {
  const base = `/api/chat/${slug}/_deck`;
  return {
    kind: 'deck',
    key: `${slug}:_deck`,
    slug,
    slide: null,
    chatPath: base,
    threadPath: `${base}/thread`,
    label: 'Whole deck',
    placeholder: 'Add a slide, reorder a section, audit the deck…',
  };
}

/**
 * A filmstrip multi-selection chatting as one constrained deck turn. Same
 * endpoint, thread and store key as deck scope — the cards land in the deck
 * thread and the one-turn-per-key gate serialises selection sends — but the
 * POST carries `selection: [ids]` so the server restricts the turn to editing
 * exactly those slides (no create/delete/reorder). `slides` are the selected
 * blocks' entries in deck order, ids already base (file-level).
 */
export function selectionScope(slug: string, slides: SlideEntry[]): ChatScope {
  const base = `/api/chat/${slug}/_deck`;
  const n = slides.length;
  return {
    kind: 'selection',
    key: `${slug}:_deck`,
    slug,
    slide: null,
    selection: slides.map((s) => s.id),
    selectionSlides: slides,
    chatPath: base,
    threadPath: `${base}/thread`,
    label: `${n} slide${n === 1 ? '' : 's'}`,
    placeholder: n === 1 ? 'Change the selected slide…' : `Change all ${n} selected slides at once…`,
  };
}
